import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const KEY = "ui-scroll:v1";

function read(): Record<string, number> {
  try {
    return JSON.parse(sessionStorage.getItem(KEY) ?? "{}");
  } catch {
    return {};
  }
}
function write(map: Record<string, number>) {
  try {
    // keep the most recent entries only
    const entries = Object.entries(map).slice(-60);
    sessionStorage.setItem(KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch {
    // ignore
  }
}

/**
 * Remembers how far the main content area was scrolled for every history entry. Going Back/Forward puts the page back where it was
 * (waiting briefly for the page's data to make it tall enough); opening something new starts at the top.
 */
export function useScrollRestoration(el: HTMLElement | null) {
  const location = useLocation();
  const type = useNavigationType();
  const current = useRef(location.key);

  // keep saving the position of the entry being viewed
  useEffect(() => {
    if (!el) return;
    let timer: number | undefined;
    const save = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        const map = read();
        map[current.current] = el.scrollTop;
        write(map);
      }, 120);
    };
    el.addEventListener("scroll", save, { passive: true });
    return () => {
      el.removeEventListener("scroll", save);
      window.clearTimeout(timer);
    };
  }, [el]);

  useLayoutEffect(() => {
    current.current = location.key;
    if (!el) return;
    if (type !== "POP") {
      el.scrollTop = 0;
      return;
    }
    const target = read()[location.key];
    if (!target) {
      el.scrollTop = 0;
      return;
    }
    // the page may still be loading — try until the content is tall enough (or give up after ~2.5s)
    let tries = 0;
    const attempt = () => {
      el.scrollTop = target;
      if (Math.abs(el.scrollTop - target) > 2 && tries++ < 25) timer = window.setTimeout(attempt, 100);
    };
    let timer = window.setTimeout(attempt, 0);
    return () => window.clearTimeout(timer);
  }, [location.key, type, el]);
}
